import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get_vehicle_detail } from "../../utils/api";
import {
  Box,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Icon,
  Badge,
  Divider,
  Skeleton,
  useDisclosure,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import ImageModal from "../../components/ImageModal";
import {
  FiTruck,
  FiUser,
  FiPhone,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

// ── Reusable: section header ──────────────────────────────────────────────────
const SectionHeader = ({ icon, title, color = "blue.500", count }) => (
  <Flex align="center" gap={3} mb={4}>
    <Box
      w="34px"
      h="34px"
      borderRadius="9px"
      bg={`${color.split(".")[0]}.50`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} color={color} boxSize={4} />
    </Box>
    <Flex align="center" gap={2}>
      <Text fontWeight="700" fontSize="md" color="gray.700">
        {title}
      </Text>
      {count != null && (
        <Badge colorScheme="blue" borderRadius="8px" px={2} fontSize="xs">
          {count}
        </Badge>
      )}
    </Flex>
  </Flex>
);

// ── Reusable: info field ──────────────────────────────────────────────────────
const InfoField = ({ label, value }) => (
  <Box>
    <Text
      fontSize="10px"
      fontWeight="700"
      color="gray.400"
      textTransform="uppercase"
      letterSpacing="0.6px"
      mb="3px"
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="500" color="gray.800" wordBreak="break-word">
      {value || "N/A"}
    </Text>
  </Box>
);

// ── Yes/No field ──────────────────────────────────────────────────────────────
const YesNoField = ({ label, value }) => (
  <Box>
    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.6px" mb="3px">
      {label}
    </Text>
    <Flex align="center" gap={1}>
      <Icon
        as={value == 1 ? FiCheckCircle : FiXCircle}
        color={value == 1 ? "green.500" : "red.400"}
        boxSize={4}
      />
      <Text fontSize="sm" fontWeight="600" color={value == 1 ? "green.600" : "red.500"}>
        {value == 1 ? "Yes" : "No"}
      </Text>
    </Flex>
  </Box>
);

// ── Main ──────────────────────────────────────────────────────────────────────
const VehicleDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");

  useEffect(() => {
    get_vehicle_detail(location.state, setLoading).then((el) => {
      if (el.success) setData(el.reponselist);
    });
  }, []);

  return (
    <>
      {/* ── Top Bar ──────────────────────────────────────────────────────── */}
      <Card
        display="flex"
        flexDirection="row"
        alignItems="center"
        justify="space-between"
        paddingInline={5}
        paddingBlock={3}
        boxShadow="sm"
        borderRadius="12px"
        mb={1}
      >
        <Flex align="center" gap={3}>
          <Box
            w="36px"
            h="36px"
            borderRadius="9px"
            bg="blue.50"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Icon as={FiTruck} color="blue.500" boxSize={4} />
          </Box>
          <Box>
            <Heading fontSize="lg" color="gray.800">
              Vehicle Details
            </Heading>
            {data.vehicleno && (
              <Text fontSize="xs" color="gray.400">
                {data.vehicleno}
              </Text>
            )}
          </Box>
        </Flex>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title="Back"
          height="34px"
          b_color="#374151"
          onClick={() =>
            navigate("/vehicle-list", {
              state: { page: location.state?.page, rows: location.state?.rows },
            })
          }
        />
      </Card>

      {/* ── Loading ───────────────────────────────────────────────────────── */}
      {loading ? (
        <VStack spacing={4} align="stretch" mt={4}>
          <Card borderRadius="14px" p={6}>
            <Flex gap={3} mb={5}>
              <Skeleton h="28px" w="120px" borderRadius="8px" />
              <Skeleton h="28px" w="80px" borderRadius="8px" />
            </Flex>
            <Skeleton h="14px" w="30%" mb={5} borderRadius="8px" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              {[...Array(8)].map((_, i) => (
                <Box key={i}>
                  <Skeleton h="10px" mb={2} w="40%" borderRadius="6px" />
                  <Skeleton h="14px" w="70%" borderRadius="6px" />
                </Box>
              ))}
            </SimpleGrid>
          </Card>
          <Card borderRadius="14px" p={6}>
            <Skeleton h="14px" w="25%" mb={4} borderRadius="8px" />
            <Skeleton h="80px" borderRadius="10px" />
          </Card>
        </VStack>
      ) : (
        <VStack spacing={4} align="stretch" mt={4}>

          {/* ── Vehicle Info ─────────────────────────────────────────────── */}
          <Card
            bg="white"
            borderRadius="14px"
            boxShadow="0 2px 8px rgba(0,0,0,0.06)"
            border="1.5px solid"
            borderColor="gray.100"
            p={6}
          >
            {/* Vehicle number + type badges */}
            <Flex align="center" gap={2} mb={5} flexWrap="wrap">
              {data.vehicleno && (
                <Badge colorScheme="blue" px={3} py={1} borderRadius="8px" fontSize="sm" fontWeight="700">
                  {data.vehicleno}
                </Badge>
              )}
              {data.type && (
                <Badge colorScheme="orange" px={3} py={1} borderRadius="8px" fontSize="sm" fontWeight="700">
                  {data.type?.toUpperCase()}
                </Badge>
              )}
            </Flex>

            <SectionHeader icon={FiTruck} title="Vehicle Information" color="blue.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5} mb={5}>
              <InfoField label="Model" value={data.model} />
              <InfoField label="Make" value={data.make} />
              <InfoField label="Chassis No" value={data.chassis} />
              <InfoField label="Capacity" value={data.capacity} />
              <YesNoField label="Supply" value={data.supply} />
              <YesNoField label="Delivery" value={data.delivery} />
            </SimpleGrid>

            <Divider mb={5} />

            <SectionHeader icon={FiUser} title="Assigned Partner" color="purple.500" />
            <SimpleGrid columns={[1, 2, 3]} spacing={5}>
              <InfoField label="DP Name" value={data.delivery_partner_name} />
              <InfoField label="Owner Contact" value={data.delivery_partner_mobile} />
              <InfoField label="Emergency Contact" value={data.emergency_contact_number} />
            </SimpleGrid>
          </Card>

          {/* ── Documents ────────────────────────────────────────────────── */}
          <Card
            bg="white"
            borderRadius="14px"
            boxShadow="0 2px 8px rgba(0,0,0,0.06)"
            border="1.5px solid"
            borderColor="gray.100"
            p={6}
          >
            <SectionHeader
              icon={FiFileText}
              title="Document Details"
              color="purple.500"
              count={data?.documents?.length}
            />

            {!data?.documents || data.documents.length === 0 ? (
              <Box
                bg="gray.50"
                borderRadius="10px"
                p={6}
                textAlign="center"
                border="1.5px dashed"
                borderColor="gray.200"
              >
                <Text fontSize="sm" color="gray.400">
                  No documents available
                </Text>
              </Box>
            ) : (
              <SimpleGrid columns={[1, 2]} spacing={3}>
                {data.documents.map((el, i) => (
                  <Box
                    key={i}
                    bg="gray.50"
                    borderRadius="10px"
                    border="1.5px solid"
                    borderColor="gray.100"
                    p={4}
                  >
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <Box flex={1} mr={3}>
                        <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mb="2px">
                          Document Name
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>
                          {el.name || "N/A"}
                        </Text>
                      </Box>
                      <CustomButton
                        title="View"
                        height="28px"
                        onClick={() => { setUrl(el.url); onOpen(); }}
                      />
                    </Flex>
                    <SimpleGrid columns={2} spacing={3}>
                      <InfoField label="Doc Type" value={el.doc_name} />
                      <InfoField label="Doc Number" value={el.document_number} />
                    </SimpleGrid>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </Card>

        </VStack>
      )}

      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default VehicleDetail;
